import { useEffect, useMemo } from 'react';
import { fmtCents, fmtDateShort, todayIso } from '../format.js';
import { groupPairs } from '../txn-utils.js';

export default function Dashboard({ state, lookups, loadFullHistory }) {
  // Lazy-load the full transaction ledger on mount. Without this, the
  // Checking balance and MTD spend totals would silently undercount once
  // the ledger grows past the bootstrap's 50-most-recent cap.
  useEffect(() => {
    if (state.allTransactions === null && loadFullHistory) {
      loadFullHistory().catch(() => {/* surfaces via UI fallback */});
    }
  }, [state.allTransactions, loadFullHistory]);

  // Use the full ledger if loaded, otherwise fall back to the bootstrap recents.
  // Falling back is fine for first paint — once the full set lands, totals
  // refresh automatically.
  const txns = state.allTransactions ?? state.transactions;

  const today = todayIso();
  const monthStart = today.slice(0, 7) + '-01';
  const weekDays = useMemo(() => {
    // Use Kansas City local time (America/Chicago) so the week boundary
    // rolls over at local midnight Sunday, not UTC midnight.
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Chicago',
      year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
    });
    const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
    const todayLocal = `${parts.year}-${parts.month}-${parts.day}`;
    const weekdayIdx = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(parts.weekday);
    // Subtract weekdayIdx days to get back to Sunday
    const [y, m, d] = todayLocal.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() - weekdayIdx);
    return dt.toISOString().slice(0, 10);
  }, []);

  // Compute current checking balance as pure ledger sum.
  // Every starting balance MUST be represented as an "Opening Balance" transaction.
  const checkingId = state.accounts.find(a => a.name === 'Checking')?.id;
  const checkingBalance = useMemo(() => {
    if (!checkingId) return 0;
    return txns
      .filter(t => t.account_id === checkingId)
      .reduce((sum, t) => sum + t.amount_cents, 0);
  }, [txns, checkingId]);

  // Total active debt from debts table (live where linked)
  const totalDebt = state.debts
    .filter(d => !d.archived)
    .reduce((sum, d) => sum + (d.live_balance_cents ?? d.current_cents), 0);

  // Emergency fund goal — use live balance if linked
  const efGoal = state.goals.find(g => /emergency/i.test(g.name));
  const efBalance = efGoal ? (efGoal.live_balance_cents ?? efGoal.current_cents) : 0;
  const efPct = efGoal && efGoal.target_cents
    ? Math.round((efBalance / efGoal.target_cents) * 1000) / 10
    : 0;

  // Discretionary tracking (this week)
  const discCategoryNames = (lookups.setting('disc_categories') ?? '')
    .split(',').map(s => s.trim()).filter(Boolean);
  const discCategoryIds = state.categories
    .filter(c => discCategoryNames.includes(c.name))
    .map(c => c.id);

  const weeklyBudgetCents = parseInt(lookups.setting('weekly_disc_budget_cents') ?? '0', 10);

  const weeklyDiscSpent = useMemo(() => {
    return txns
      .filter(t => t.occurred_on >= weekDays && t.amount_cents < 0)
      .filter(t => discCategoryIds.includes(t.category_id))
      .reduce((sum, t) => sum + Math.abs(t.amount_cents), 0);
  }, [txns, weekDays, discCategoryIds]);

  const weeklyRemaining = weeklyBudgetCents - weeklyDiscSpent;
  const weeklyPctUsed = weeklyBudgetCents ? (weeklyDiscSpent / weeklyBudgetCents) * 100 : 0;

  // Month-to-date spending (all expenses) — kept for the existing KPI tile
  const mtdSpent = useMemo(() => {
    return txns
      .filter(t => t.occurred_on >= monthStart && t.amount_cents < 0 && t.type === 'Expense')
      .reduce((sum, t) => sum + Math.abs(t.amount_cents), 0);
  }, [txns, monthStart]);

  // ────────────────────────────────────────────────────────────────────────
  // Monthly budget view — drives both the "Discretionary — This Month" card
  // and the "Month Budget" section. Logic mirrors Budgets.jsx (transfer-pair
  // dedup, Income-excluded totals) so figures match across screens.
  // ────────────────────────────────────────────────────────────────────────
  const monthKey = today.slice(0, 7); // YYYY-MM
  const dayOfMonth = parseInt(today.slice(8, 10), 10);
  const daysInMonth = useMemo(() => {
    const [y, m] = monthKey.split('-').map(Number);
    return new Date(y, m, 0).getDate(); // last day of month
  }, [monthKey]);
  const monthEnd = `${monthKey}-${String(daysInMonth).padStart(2, '0')}`;
  const monthFraction = dayOfMonth / daysInMonth; // 0..1

  // Per-category actuals for this month, respecting transfer-pair dedup.
  // (Same algorithm as Budgets.jsx → spendByCategory, kept inline rather
  // than refactored to avoid cross-file coupling for now.)
  const spendByCategory = useMemo(() => {
    const out = {};
    const seenPairs = new Set();
    for (const t of txns) {
      if (t.occurred_on < monthStart || t.occurred_on > monthEnd) continue;
      if (t.transfer_pair_id) {
        if (seenPairs.has(t.transfer_pair_id)) continue;
        // Only count the debit leg (negative). Skip the credit leg; the
        // matching debit leg will be visited later in the loop.
        if (t.amount_cents > 0) continue;
        seenPairs.add(t.transfer_pair_id);
      }
      out[t.category_id] = (out[t.category_id] ?? 0) + Math.abs(t.amount_cents);
    }
    return out;
  }, [txns, monthStart, monthEnd]);

  // Income category id(s) — excluded from expense totals so target/actual
  // reflect expense-budget health, not a meaningless income+expense sum.
  const incomeCategoryIds = useMemo(
    () => state.categories.filter(c => /income/i.test(c.name)).map(c => c.id),
    [state.categories],
  );
  const incomeIdSet = useMemo(() => new Set(incomeCategoryIds), [incomeCategoryIds]);

  // Discretionary category ids (from settings) — drives the monthly
  // discretionary card. These are the same ids used for the weekly tracker.
  const discCategoryIdSet = useMemo(() => new Set(discCategoryIds), [discCategoryIds]);

  // Build a sorted view of this month's budgets, classified by status.
  // Status buckets:
  //   'over'       actual > target
  //   'watch'      ahead of pace by >= 5% AND not yet over (mid-month splurge warning)
  //   'on_track'   within ±5% of pace (or under, but with some activity)
  //   'inactive'   actual === 0 and we're <80% through the month (e.g., bill not due yet)
  // Schedule-driven categories: their spend follows recurring rules, not
  // daily behavior, so "ahead of pace" warnings on them are noise. We treat
  // them as on_track when within budget and over only when strictly over.
  function isScheduleDriven(name) {
    return /^Savings\b/i.test(name) || /^Debt Payment\b/i.test(name) || /^Sinking Fund/i.test(name);
  }

  function classify(target, actual, name) {
    if (target <= 0) return 'inactive';
    if (actual > target) return 'over';
    if (actual === 0 && monthFraction < 0.8) return 'inactive';
    if (isScheduleDriven(name)) return 'on_track'; // schedule, not behavior
    const expected = target * monthFraction;
    if (expected <= 0) return 'on_track';
    const ratio = actual / expected; // 1.0 = exactly on pace
    if (ratio >= 1.05) return 'watch';
    return 'on_track';
  }
  const STATUS_RANK = { over: 0, watch: 1, on_track: 2, inactive: 3 };

  const monthBudgetRows = useMemo(() => {
    const rows = state.budgets
      .filter(b => b.month === monthKey)
      .map(b => {
        const target = b.target_cents;
        const actual = spendByCategory[b.category_id] ?? 0;
        const isIncome = incomeIdSet.has(b.category_id);
        const name = lookups.catName(b.category_id);
        const status = isIncome ? 'on_track' : classify(target, actual, name);
        const expected = target * monthFraction;
        return {
          id: b.id,
          category_id: b.category_id,
          name,
          target,
          actual,
          isIncome,
          status,
          delta: actual - expected, // expense delta: + means over pace
          pct: target ? (actual / target) * 100 : 0,
        };
      });
    // Sort: status bucket, then biggest dollar problems first within each.
    rows.sort((a, b) => {
      if (a.isIncome !== b.isIncome) return a.isIncome ? 1 : -1; // income last
      const ra = STATUS_RANK[a.status], rb = STATUS_RANK[b.status];
      if (ra !== rb) return ra - rb;
      // Within a bucket: over → bigger overage first, watch → bigger delta,
      // on_track → bigger remaining, inactive → bigger target first.
      if (a.status === 'over') return (b.actual - b.target) - (a.actual - a.target);
      if (a.status === 'watch') return b.delta - a.delta;
      if (a.status === 'on_track') return (b.target - b.actual) - (a.target - a.actual);
      return b.target - a.target;
    });
    return rows;
  }, [state.budgets, spendByCategory, incomeIdSet, monthKey, monthFraction, lookups]);

  // Section totals (expenses only)
  const expenseRows = monthBudgetRows.filter(r => !r.isIncome);
  const monthTargetTotal = expenseRows.reduce((s, r) => s + r.target, 0);
  const monthActualTotal = expenseRows.reduce((s, r) => s + r.actual, 0);
  const monthExpectedTotal = monthTargetTotal * monthFraction;
  const monthPaceDelta = monthActualTotal - monthExpectedTotal; // + = over pace
  const overCount = expenseRows.filter(r => r.status === 'over').length;
  const watchCount = expenseRows.filter(r => r.status === 'watch').length;

  // Income figures for the projected-net caption
  const incomeRow = monthBudgetRows.find(r => r.isIncome);
  const incomeTarget = incomeRow ? incomeRow.target : 0;
  const incomeActual = incomeRow ? incomeRow.actual : 0;
  const projectedNet = incomeTarget - monthTargetTotal; // planned cash flow

  // Discretionary monthly rollup — same dedup rules as the weekly tracker.
  // Sums across the disc categories' budgets for THIS month.
  const discMonth = useMemo(() => {
    const discBudgets = state.budgets.filter(
      b => b.month === monthKey && discCategoryIdSet.has(b.category_id),
    );
    const target = discBudgets.reduce((s, b) => s + b.target_cents, 0);
    const actual = discBudgets.reduce(
      (s, b) => s + (spendByCategory[b.category_id] ?? 0),
      0,
    );
    const expected = target * monthFraction;
    return {
      target,
      actual,
      expected,
      delta: actual - expected,
      remaining: target - actual,
      categoryCount: discBudgets.length,
    };
  }, [state.budgets, monthKey, discCategoryIdSet, spendByCategory, monthFraction]);

  return (
    <div className="screen">
      <h1>Dashboard</h1>
      <div className="muted tiny" style={{ marginBottom: 20 }}>
        Today is {fmtDateShort(today)}
      </div>

      {/* Top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <Kpi label="Checking" value={fmtCents(checkingBalance)}
             tone={checkingBalance >= 100000 ? 'pos' : 'warn'} />
        <Kpi label="Total Debt" value={fmtCents(totalDebt)} tone="warn" />
        <Kpi
          label="Emergency Fund"
          value={fmtCents(efBalance)}
          sub={efGoal ? `${efPct}% of ${fmtCents(efGoal.target_cents)}` : ''}
          tone="info"
        />
        <Kpi label="MTD Expenses" value={fmtCents(mtdSpent)} sub={fmtDateShort(monthStart) + ' →'} />
      </div>

      {/* Discretionary tracker */}
      {weeklyBudgetCents > 0 && (
        <div className="card">
          <h2 style={{ margin: 0, marginBottom: 12 }}>Discretionary — This Week</h2>
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className={'amount ' + (weeklyRemaining < 0 ? 'neg' : 'pos')}>
              {fmtCents(weeklyRemaining)}
            </span>
            <span className="muted tiny">
              of {fmtCents(weeklyBudgetCents)} left
            </span>
          </div>
          <ProgressBar pct={Math.min(100, weeklyPctUsed)}
                       tone={weeklyPctUsed >= 100 ? 'neg' : weeklyPctUsed >= 75 ? 'warn' : 'pos'} />
          <div className="tiny muted" style={{ marginTop: 8 }}>
            {fmtCents(weeklyDiscSpent)} spent in {discCategoryNames.length} categories
          </div>
        </div>
      )}

      {/* Discretionary — This Month. Pairs with the weekly card above and
          uses the discretionary categories' MONTHLY budgets. The pace delta
          ("ahead/behind by") is what makes it actionable: it answers "if I
          keep this rate, where does the month land?" rather than just "did
          I overspend yet?". */}
      {discMonth.target > 0 && (
        <div className="card">
          <h2 style={{ margin: 0, marginBottom: 12 }}>Discretionary — This Month</h2>
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className={'amount ' + (discMonth.remaining < 0 ? 'neg' : 'pos')}>
              {fmtCents(discMonth.remaining)}
            </span>
            <span className="muted tiny">
              of {fmtCents(discMonth.target)} left
            </span>
          </div>
          <ProgressBar
            pct={Math.min(100, discMonth.target ? (discMonth.actual / discMonth.target) * 100 : 0)}
            tone={
              discMonth.actual > discMonth.target ? 'neg'
              : discMonth.delta > discMonth.target * 0.05 ? 'warn'
              : 'pos'
            }
          />
          <div className="row between tiny muted" style={{ marginTop: 8 }}>
            <span>{fmtCents(discMonth.actual)} spent · day {dayOfMonth} of {daysInMonth}</span>
            <span style={{
              color: discMonth.delta > 0 ? 'var(--red)' : 'var(--green)',
              fontWeight: 500,
            }}>
              {discMonth.delta > 0
                ? `${fmtCents(Math.abs(discMonth.delta))} ahead of pace`
                : `${fmtCents(Math.abs(discMonth.delta))} under pace`}
            </span>
          </div>
        </div>
      )}

      {/* Month Budget — heads-up view across all expense categories with a
          budget for the current month. Sorted: over → watch → on_track →
          inactive, so the things demanding attention are at the top. The
          summary line answers "how does the month land?" with a projected
          net (target income vs target expenses) and an actual pace delta. */}
      {expenseRows.length > 0 && (
        <>
          <h2>Month Budget — {fmtMonthLabel(monthKey)}</h2>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="row between" style={{ alignItems: 'baseline' }}>
              <div>
                <div className="amount" style={{ fontSize: 18, fontWeight: 600 }}>
                  {fmtCents(monthActualTotal)}
                  <span className="muted" style={{ fontSize: 14, fontWeight: 400 }}>
                    {' '}/ {fmtCents(monthTargetTotal)}
                  </span>
                </div>
                <div className="tiny muted" style={{ marginTop: 2 }}>
                  spent of expense budget · day {dayOfMonth}/{daysInMonth}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="amount" style={{
                  fontSize: 14, fontWeight: 600,
                  color: monthPaceDelta > 0 ? 'var(--red)' : 'var(--green)',
                }}>
                  {monthPaceDelta > 0 ? '+' : '−'}{fmtCents(Math.abs(monthPaceDelta))}
                </div>
                <div className="tiny muted" style={{ marginTop: 2 }}>
                  {monthPaceDelta > 0 ? 'ahead of pace' : 'under pace'}
                </div>
              </div>
            </div>
            <ProgressBar
              pct={Math.min(100, monthTargetTotal ? (monthActualTotal / monthTargetTotal) * 100 : 0)}
              tone={
                monthActualTotal > monthTargetTotal ? 'neg'
                : monthPaceDelta > monthTargetTotal * 0.05 ? 'warn'
                : 'pos'
              }
            />
            <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.45 }}>
              {overCount > 0 && <><b style={{ color: 'var(--red)' }}>{overCount} over</b> · </>}
              {watchCount > 0 && <><b style={{ color: 'var(--amber)' }}>{watchCount} watch</b> · </>}
              Income: {fmtCents(incomeActual)} of {fmtCents(incomeTarget)} ·{' '}
              Plan: {projectedNet >= 0 ? '+' : '−'}{fmtCents(Math.abs(projectedNet))} net
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            {monthBudgetRows.map(r => (
              <BudgetRow key={r.id} row={r} monthFraction={monthFraction} />
            ))}
          </div>
        </>
      )}

      {/* Goals snapshot */}
      <h2>Goals</h2>
      {state.goals.filter(g => !g.archived && g.dashboard_visible !== 0).map(g => {
        const bal = g.live_balance_cents ?? g.current_cents;
        return (
          <div className="card" key={g.id} style={{ marginBottom: 8 }}>
            <div className="row between" style={{ marginBottom: 6 }}>
              <strong>{g.name}</strong>
              <span className="muted tiny">
                {fmtCents(bal)} / {fmtCents(g.target_cents)}
              </span>
            </div>
            <ProgressBar
              pct={g.target_cents ? Math.min(100, (bal / g.target_cents) * 100) : 0}
              tone="info"
            />
          </div>
        );
      })}

      {/* Recent activity */}
      <h2>Recent Activity</h2>
      <div className="card" style={{ padding: 0 }}>
        {groupPairs(state.transactions).slice(0, 6).map(t => (
          <div key={t.id} className="list-item">
            <div className="grow">
              <div>
                {t.description}
                {t._pair && <span className="pill info" style={{ marginLeft: 8 }}>Transfer</span>}
              </div>
              <div className="meta">
                {fmtDateShort(t.occurred_on)} · {lookups.catName(t.category_id)} ·{' '}
                {t._pair
                  ? <>{lookups.acctName(t._pair.from_account_id)} → {lookups.acctName(t._pair.to_account_id)}</>
                  : lookups.acctName(t.account_id)}
              </div>
            </div>
            <span className={'amount ' + (t.amount_cents >= 0 ? 'pos' : 'neg')}>
              {fmtCents(t._pair ? -Math.abs(t._pair.debit.amount_cents) : t.amount_cents)}
            </span>
          </div>
        ))}
        {state.transactions.length === 0 && (
          <div className="empty">
            <div className="icon">∅</div>
            <div>No transactions yet</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div className="muted tiny" style={{ marginBottom: 4 }}>{label}</div>
      <div className={'amount ' + (tone === 'pos' ? 'pos' : tone === 'neg' ? 'neg' : '')}
           style={{ fontSize: 20, fontWeight: 600 }}>
        {value}
      </div>
      {sub && <div className="tiny muted" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct, tone }) {
  const color = tone === 'neg' ? 'var(--red)'
              : tone === 'warn' ? 'var(--amber)'
              : tone === 'info' ? 'var(--blue)'
              : 'var(--green)';
  return (
    <div style={{
      background: 'var(--bg2)',
      height: 6,
      borderRadius: 3,
      overflow: 'hidden',
    }}>
      <div style={{
        width: `${Math.max(0, Math.min(100, pct))}%`,
        height: '100%',
        background: color,
        transition: 'width 200ms ease',
      }} />
    </div>
  );
}

// Compact per-budget row for the Dashboard's Month Budget section. The
// detail modal lives on the Budgets screen — these rows are read-only.
// The "delta" line below the bar is what makes this useful at a glance:
//   over    →  red, "+$X over budget"
//   watch   →  amber, "+$X ahead of pace"
//   on_track→  green, either "% used" or "$X left this month"
//   inactive→  muted, "not yet active" (e.g., bill not due)
function BudgetRow({ row, monthFraction }) {
  const { name, target, actual, status, isIncome, delta, pct } = row;

  // Income gets a separate visual treatment — it's progress TOWARD a goal,
  // not consumption AGAINST a budget.
  if (isIncome) {
    const incPct = target ? Math.min(100, (actual / target) * 100) : 0;
    return (
      <div className="list-item" style={{
        flexDirection: 'column', alignItems: 'stretch', gap: 6,
        borderBottom: '1px solid var(--border2)',
      }}>
        <div className="row between">
          <span style={{ fontWeight: 500 }}>
            {name}
            <span className="pill info" style={{ marginLeft: 8, fontSize: 10 }}>income</span>
          </span>
          <span className="amount">
            {fmtCents(actual)}
            <span className="muted" style={{ fontWeight: 400 }}> / {fmtCents(target)}</span>
          </span>
        </div>
        <ProgressBar pct={incPct} tone="info" />
      </div>
    );
  }

  const barTone = status === 'over' ? 'neg'
                : status === 'watch' ? 'warn'
                : status === 'inactive' ? 'info'
                : 'pos';

  let footnote;
  if (status === 'over') {
    footnote = (
      <span style={{ color: 'var(--red)', fontWeight: 500 }}>
        +{fmtCents(actual - target)} over budget
      </span>
    );
  } else if (status === 'watch') {
    footnote = (
      <span style={{ color: 'var(--amber)', fontWeight: 500 }}>
        +{fmtCents(delta)} ahead of pace
      </span>
    );
  } else if (status === 'inactive') {
    footnote = <span className="muted">not yet active</span>;
  } else {
    // On track: lead with remaining if < 75% used, otherwise % used
    if (pct < 75) {
      footnote = <span style={{ color: 'var(--green)' }}>{fmtCents(target - actual)} left</span>;
    } else {
      footnote = <span style={{ color: 'var(--green)' }}>{Math.round(pct)}% used</span>;
    }
  }

  return (
    <div className="list-item" style={{
      flexDirection: 'column', alignItems: 'stretch', gap: 6,
      borderBottom: '1px solid var(--border2)',
    }}>
      <div className="row between">
        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        <span className={'amount ' + (status === 'over' ? 'neg' : '')}>
          {fmtCents(actual)}
          <span className="muted" style={{ fontWeight: 400 }}> / {fmtCents(target)}</span>
        </span>
      </div>
      <ProgressBar pct={Math.min(100, pct)} tone={barTone} />
      <div className="row between tiny" style={{ marginTop: 2 }}>
        <span className="muted">{Math.round(monthFraction * 100)}% through month</span>
        {footnote}
      </div>
    </div>
  );
}

// "2026-05" → "May 2026"
function fmtMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' });
}
